import {
  TextField,
  Autocomplete,
  createFilterOptions,
  Box,
  Typography,
} from "@mui/material";
import { startCase } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Reference } from "../../../types/reference";
import useLocations from "../../../hooks/queries/useLocations";
import { useOrganizationReference } from "../../../pages/facility/hooks/useOrganization";
import { Location, LocationStatus } from "../../../types/location";
import {
  createReference,
  getIdFromRefValue,
} from "../../../utils/DataTransformationUtils";
import locationApi from "../../../api/providers/locationApi";

const filter = createFilterOptions<Location | Location[]>({
  stringify: (option: Location) => `${option.name} ${option.alias?.join(" ")}`,
});

type LocationSelectProps = {
  disabled?: boolean;
  fieldSize?: "small" | "medium";
  label?: string;
  shouldDisableLocation?: (location: Location) => boolean;
  status?: LocationStatus[];
} & (
  | {
      isMultiple?: true;
      onSelectLocation: (locations: Reference[]) => void;
      location: Reference[];
    }
  | {
      isMultiple?: false;
      onSelectLocation: (location: Reference) => void;
      location: Reference;
    }
);

export default function LocationSelect({
  disabled,
  fieldSize,
  onSelectLocation,
  location,
  label = "Select Location (Room, Bed, etc.)",
  shouldDisableLocation = (l: Location) => false,
  status,
  isMultiple = false,
}: LocationSelectProps) {
  const organizationRef = useOrganizationReference();
  const [selected, setSelected] = useState<Location | Location[]>([]);
  const selectedRef = useRef<string>(JSON.stringify([]));
  const { locations, isLoading } = useLocations(
    {
      organizationId: organizationRef?.identifier,
      ...(status ? { status } : {}),
      limit: 0,
    },
    {
      enabled: !!organizationRef?.identifier,
    },
  );

  const getSelectedLocation = useCallback(
    async (nextLocation: Reference) => {
      try {
        const savedLocation = locations.find(
          (loc) => loc.id === getIdFromRefValue(nextLocation.reference),
        );
        if (savedLocation) {
          return savedLocation;
        }
        // get location from api
        const res = await locationApi.getLocation(
          getIdFromRefValue(nextLocation.reference),
        );
        return res;
      } catch (error) {
        console.error("Error getting selected location", error);
        return null;
      }
    },
    [locations],
  );

  const setSelectedLocations = useCallback(
    async (nextLocations: Reference | Reference[]) => {
      if (Array.isArray(nextLocations)) {
        setSelected(
          await Promise.all(nextLocations.map((l) => getSelectedLocation(l))),
        );
      } else {
        setSelected(await getSelectedLocation(nextLocations));
      }
    },
    [getSelectedLocation],
  );

  useEffect(() => {
    const serialized = JSON.stringify(location ?? []);
    if (serialized !== selectedRef.current) {
      selectedRef.current = serialized;
      setSelectedLocations(location);
    }
  }, [location, setSelectedLocations]);

  return (
    <Autocomplete
      disabled={disabled}
      multiple={isMultiple}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        return filtered;
      }}
      value={selected}
      loading={isLoading}
      options={locations}
      getOptionLabel={(o: Location) => {
        // Value selected with enter, right from the input
        if (typeof o === "string") {
          return o;
        }

        // Regular option
        return o?.name || "";
      }}
      clearOnBlur
      isOptionEqualToValue={(o: Location, v: Location) => o?.id === v?.id}
      size={fieldSize}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Select from options below"
        />
      )}
      autoHighlight
      onChange={(e, newValue) => {
        if (isMultiple) {
          if (Array.isArray(newValue)) {
            onSelectLocation(
              newValue.map((l: Location) =>
                createReference("Location", l.id, l.name),
              ),
            );
          }
        } else {
          if (Array.isArray(newValue)) {
            return;
          }

          if (!newValue) {
            onSelectLocation(null);
            return;
          }

          if (typeof newValue === "string") {
            return;
          }

          if (newValue.name) {
            onSelectLocation(
              createReference(
                "Location",
                newValue.id,
                newValue.name,
              ) as Reference[] & Reference,
            );
          }
        }
      }}
      getOptionDisabled={(option) => shouldDisableLocation(option as Location)}
      renderOption={(props, option: Location) => (
        <Box component="li" {...props}>
          <Typography variant="body2">
            {option.name}{" "}
            {option.alias?.length ? `(${option.alias?.join(" ")})` : ""}
            {option.status && option.status !== "active" && (
              <Typography component="span" color="warning.main">
                {` - ${startCase(option.status)}`}
              </Typography>
            )}
            {option.operationalStatus?.code &&
              option.operationalStatus?.code !== "U" && (
                <Typography component="span" color="error.main">
                  {` - ${option.operationalStatus?.display}`}
                </Typography>
              )}
          </Typography>
        </Box>
      )}
    />
  );
}
